import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from '@/i18n'
import { useAppStore } from '@/store'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { trackEvent } from '@/utils/analytics'
import StarRating from './StarRating'

/** Category ids match the jsonb keys persisted in `help_feedback.ratings`. */
export const RATING_CATEGORIES = ['response_time', 'helpfulness', 'clarity'] as const

/**
 * "Rate this Guide" view: one 1–5 star row per category, submit enabled
 * once every category is rated.
 */
const RatingView = () => {
  const { t } = useTranslation('help_agent')
  const location = useLocation()
  const helpRatings = useAppStore((s) => s.helpRatings)
  const setHelpRating = useAppStore((s) => s.setHelpRating)
  const submitHelpFeedback = useAppStore((s) => s.submitHelpFeedback)
  const [submitting, setSubmitting] = useState(false)
  const [submitFailed, setSubmitFailed] = useState(false)

  const allRated = RATING_CATEGORIES.every((category) => (helpRatings[category] ?? 0) > 0)

  const submit = async () => {
    setSubmitting(true)
    setSubmitFailed(false)
    const ok = await submitHelpFeedback(location.pathname)
    setSubmitting(false)
    if (ok) {
      trackEvent('help_agent-feedback_submitted', { ratings: helpRatings })
    } else {
      setSubmitFailed(true)
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4 text-center">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-dark-text">
          {t('feedback.rate_title')}
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">{t('feedback.rate_subtitle')}</p>
      </div>
      {RATING_CATEGORIES.map((category) => (
        <div key={category} className="flex flex-col gap-1.5">
          <div className="text-sm font-medium text-gray-900 dark:text-dark-text">
            {t(`feedback.categories.${category}.label`)}
          </div>
          <div className="text-xs text-muted-foreground">
            {t(`feedback.categories.${category}.hint`)}
          </div>
          <StarRating
            value={helpRatings[category] ?? 0}
            onChange={(value) => setHelpRating(category, value)}
            label={t(`feedback.categories.${category}.label`)}
          />
        </div>
      ))}
      {submitFailed && <p className="text-xs text-red-600">{t('feedback.submit_error')}</p>}
      <IntuiButton
        onClick={() => void submit()}
        disabled={!allRated}
        loading={submitting}
        className="mx-auto mt-auto"
      >
        {t('feedback.submit')}
      </IntuiButton>
    </div>
  )
}

export default RatingView
